import { AppAPI, } from '@moderepo/mode-apis';
import { searchParamsToString, } from '@moderepo/mode-data-state-base';
/**
 * The types of action that can be dispatched to update this sub state.
 */
export var EntitiesActionType;
(function (EntitiesActionType) {
    EntitiesActionType["SET_ENTITY_CLASS"] = "set entity class";
    EntitiesActionType["SET_ENTITY_CLASSES"] = "set entity classes";
    EntitiesActionType["CLEAR_ENTITY_CLASSES"] = "clear entity classes";
    EntitiesActionType["SET_ENTITY"] = "set entity";
    EntitiesActionType["SET_ENTITIES"] = "set entities";
    EntitiesActionType["CLEAR_ENTITIES"] = "clear entities";
})(EntitiesActionType || (EntitiesActionType = {}));
export const setEntityClasses = (projectId, entityClasses) => {
    return {
        type: EntitiesActionType.SET_ENTITY_CLASSES,
        projectId,
        entityClasses,
    };
};
export const setEntityClass = (projectId, entityClass) => {
    return {
        type: EntitiesActionType.SET_ENTITY_CLASS,
        projectId,
        entityClass,
    };
};
export const clearEntityClasses = (projectId) => {
    return {
        type: EntitiesActionType.CLEAR_ENTITY_CLASSES,
        projectId,
    };
};
export const setEntities = (projectId, entities, searchParams) => {
    return {
        type: EntitiesActionType.SET_ENTITIES,
        projectId,
        entities,
        searchParams,
    };
};
export const setEntity = (projectId, entity) => {
    return {
        type: EntitiesActionType.SET_ENTITY,
        projectId,
        entity,
    };
};
export const clearEntities = (projectId) => {
    return {
        type: EntitiesActionType.CLEAR_ENTITIES,
        projectId,
    };
};
/**
 * Get multiple entity classes belonged to a Project
 * @param projectId - The id of the project to look for
 */
export const fetchEntityClasses = (projectId) => {
    return async (dataDispatch) => {
        const entityClasses = await AppAPI.getInstance().getEntityClasses(projectId);
        await dataDispatch(setEntityClasses(projectId, entityClasses));
    };
};
/**
 * Get an entity by id. This action will also update the app state.
 *
 * @param entityClassId - The ID of the entity we want to get
 */
export const fetchEntityClassById = (projectId, entityClassId) => {
    return async (dataDispatch) => {
        const entityClass = await AppAPI.getInstance().getEntityClassById(projectId, entityClassId);
        await dataDispatch(setEntityClass(projectId, entityClass));
    };
};
/**
 * Create entity classes for the specified project
 * @param projectId - The id of the project to look for
 * @param entityClasses - The array of entity classes object to create
 */
export const createEntityClasses = (projectId, entityClasses) => {
    return async (dataDispatch) => {
        await AppAPI.getInstance().createEntityClasses(projectId, entityClasses);
        // Refetch all entity classes
        await dataDispatch(fetchEntityClasses(projectId));
    };
};
/**
 * Create one entity class for the specified project
 * @param projectId - The id of the project to look for
 * @param entityClass - The entity class object to create
 */
export const createEntityClass = (projectId, entityClass) => {
    return async (dataDispatch) => {
        await AppAPI.getInstance().createEntityClass(projectId, entityClass);
        // Refetch all entity classes
        await dataDispatch(fetchEntityClasses(projectId));
    };
};
/**
 * Update an existing entity class
 * @param projectId - The id of the project to look for
 * @param entityClass - The entity class object to create
 */
export const updateEntityClass = (projectId, entityClassId, entityClass) => {
    return async (dataDispatch) => {
        await AppAPI.getInstance().updateEntityClass(projectId, entityClassId, entityClass);
        await dataDispatch(fetchEntityClassById(projectId, entityClassId));
    };
};
/**
 * Delete an entity class
 * @param projectId - The id of the project to look for
 * @param entityClasses - The array of entity classes object to create
 */
export const deleteEntityClass = (projectId, entityClassId) => {
    return async (dataDispatch) => {
        await AppAPI.getInstance().deleteEntityClass(projectId, entityClassId);
        // Refetch all entity classes
        await dataDispatch(fetchEntityClasses(projectId));
    };
};
/**
 * Get multiple entities belonged to a Project
 * @param projectId - The id of the project to look for
 */
export const fetchEntities = (projectId, filters) => {
    return async (dataDispatch) => {
        const modifiedFilters = {
            ...filters,
            // change pageSize/pageNumber to skip/limit
            skip: filters.pageNumber && filters.pageSize ? filters.pageNumber * filters.pageSize : undefined,
            limit: filters.pageSize ? filters.pageSize : undefined,
            // exclude pageSize/pageNumber
            pageNumber: undefined,
            pageSize: undefined,
        };
        const entities = await AppAPI.getInstance().getEntities(projectId, modifiedFilters);
        await dataDispatch(setEntities(projectId, entities, searchParamsToString(filters)));
    };
};
/**
 * Get an entity by id. This action will also update the app state.
 *
 * @param entityId - The ID of the entity we want to get
 */
export const fetchEntityById = (projectId, entityId) => {
    return async (dataDispatch) => {
        const entity = await AppAPI.getInstance().getEntityById(projectId, entityId);
        await dataDispatch(setEntity(projectId, entity));
    };
};
/**
 * Create an entity.
 * @param projectId - The id of the project the entity belonged to
 * @param params - The attributes and their values to be updated
 */
export const createEntity = (projectId, params) => {
    return async (dataDispatch) => {
        await AppAPI.getInstance().createEntity(projectId, params);
        // Need to clear the entities because the list of entities changed
        await dataDispatch(clearEntities(projectId));
    };
};
/**
 * Update an entity.
 * @param projectId - The id of the project the entity belonged to
 * @param entityId - The id of the entity which we want to update
 * @param params - The attributes and their values to be updated
 */
export const updateEntity = (projectId, entityId, params) => {
    return async (dataDispatch) => {
        await AppAPI.getInstance().updateEntity(projectId, entityId, params);
        // Need to clear the entities because updating the entity's attributes can cause the list of entities to be changed
        await dataDispatch(clearEntities(projectId));
    };
};
/**
 * Update an entity.
 * @param projectId - The id of the project the entity belonged to
 * @param entityId - The id of the entity which we want to update
 * @param params - The attributes and their values to be updated
 */
export const deleteEntity = (projectId, entityId, deleteDescendants = false) => {
    return async (dataDispatch) => {
        await AppAPI.getInstance().deleteEntity(projectId, entityId, deleteDescendants);
        // Need to clear the entities because the list of entities changed
        await dataDispatch(clearEntities(projectId));
    };
};
